import React, { useEffect, useState } from "react";
import {
    ArrowDownLeft,
    ArrowUpRight,
    Check,
    ChevronRight,
    Clock3,
    History,
    Loader2,
    Plus,
    Sparkles,
    Wallet,
} from "lucide-react";

import CommonButton from "../CommonButton";
import TextInputComp from "./TextInputComp";
import { useAlert } from "./AlertContext";

import {
    useLazyGetWalletHistoryQuery,
    useLazyGetWalletQuery,
} from "../../redux/actions/Customer/profileCustomer";

import {
    useAddTopupOfferMutation,
    useLazyGetTopUpOfferQuery,
} from "../../redux/actions/Customer/cartCustomer";
import { devNull } from "os";

const CUSTOM_AMOUNT_OFFER_ID = null;

const WalletScreen: React.FC = () => {
    const { showConfirm } = useAlert();

    const [
        getWallet,
        { data: walletSummary, isFetching: isWalletLoading },
    ] = useLazyGetWalletQuery();

    const [
        getHistory,
        { data: historyData, isFetching: isHistoryLoading },
    ] = useLazyGetWalletHistoryQuery();

    const [
        getOffers,
        { data: offersData, isFetching: isOffersLoading },
    ] = useLazyGetTopUpOfferQuery();

    const [addTopupOffer, { isLoading: isSubmittingTopup }] =
        useAddTopupOfferMutation();

    const [selectedOfferId, setSelectedOfferId] = useState<number | null>(null);
    const [customAmount, setCustomAmount] = useState<string>("");

    useEffect(() => {
        getWallet();
        getHistory();
        getOffers();
    }, []);

    const handleSelectOffer = (id: number) => {
        setSelectedOfferId(id);
        setCustomAmount("");
    };

    const handleCustomAmountChange = (value: string) => {
        // Allow only positive decimal numbers with maximum 2 decimal places
        const sanitizedValue = value.replace(/[^\d.]/g, "");

        if (/^\d*\.?\d{0,2}$/.test(sanitizedValue)) {
            setCustomAmount(sanitizedValue);
            setSelectedOfferId(null);
        }
    };

    const selectedOffer = offersData?.find(
        (offer) => offer.id === selectedOfferId
    );

    const enteredAmount = Number(customAmount || 0);

    const amountToPay = selectedOffer
        ? Number(selectedOffer.topupAmount)
        : enteredAmount;

    const bonusAmount = selectedOffer
        ? Number(selectedOffer.bonusAmount || 0)
        : 0;

    const walletCredit = amountToPay + bonusAmount;
    const getWalletBonus = (amount: number) => {
        if (amount >= 1000) return 50;
        if (amount >= 500) return 20;
        if (amount >= 200) return 10;
        if (amount >= 100) return 5;
        return 0;
    };

    const customBonus = selectedOffer
        ? Number(selectedOffer.bonusAmount || 0)
        : getWalletBonus(amountToPay);

    const finalWalletCredit = amountToPay + customBonus;

    const handleAddWalletMoney = async () => {
        const isCustomAmount = selectedOfferId === null;
        const customAmountValue = Number(customAmount);

        if (
            isCustomAmount &&
            (!customAmount ||
                isNaN(customAmountValue) ||
                customAmountValue <= 0)
        ) {
            return showConfirm({
                title: "Enter an amount",
                message: "Please select an offer or enter a valid amount to continue.",
                type: "error",
                confirmText: "Okay",
            });
        }

        try {
            const topUpPayload = {
                // If user manually enters amount, always pass ID 5
                walletTopupOfferId: selectedOfferId ?? CUSTOM_AMOUNT_OFFER_ID,

                // Selected offer = 0
                // Manual input = actual entered amount
                customAmount: selectedOfferId ? 0 : customAmountValue,
            };

            console.log("Wallet Top-up Payload:", topUpPayload);

            const response = await addTopupOffer(topUpPayload).unwrap();

            if (response?.paymentUrl && response?.transactionReference) {
                sessionStorage.setItem(
                    "wallet_topup_tx_ref",
                    response.transactionReference
                );

                sessionStorage.setItem(
                    "wallet_topup_amount",
                    String(response.amount)
                );

                sessionStorage.setItem(
                    "wallet_topup_credit",
                    String(response.walletCredit)
                );

                window.location.href = response.paymentUrl;
                return;
            }

            showConfirm({
                title: "Payment Error",
                message: "Unable to initialize the payment gateway.",
                type: "error",
                confirmText: "Okay",
            });
        } catch (err: any) {
            console.error("Wallet top-up error:", err);

            showConfirm({
                title: "Top-Up Failed",
                message:
                    err?.data?.message ||
                    "Something went wrong while starting your payment.",
                type: "error",
                confirmText: "Okay",
            });
        }
    };

    if (
        (isWalletLoading || isHistoryLoading || isOffersLoading) &&
        !walletSummary
    ) {
        return (
            <div className="min-h-[450px] flex flex-col items-center justify-center">
                <div className="w-14 h-14 rounded-2xl bg-[#F1F7ED] flex items-center justify-center mb-4">
                    <Loader2 className="w-6 h-6 text-[#2F5E2E] animate-spin" />
                </div>

                <p className="text-sm font-semibold text-gray-700">
                    Loading your wallet
                </p>

                <p className="text-xs text-gray-400 mt-1">
                    Just a moment...
                </p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-6xl mx-auto space-y-6">

            {/* ================= WALLET HERO ================= */}

            <div className="relative overflow-hidden rounded-[28px] bg-[#2F5E2E] p-6 md:p-8 text-white shadow-[0_20px_50px_rgba(47,94,46,0.18)]">

                {/* Background decoration */}
                <div className="absolute -top-24  w-64 h-64 rounded-full bg-white/[0.05]" />
                <div className="absolute -bottom-32 -right-5 w-72 h-72 rounded-full bg-[#A8D97B]/10" />

                <div className="relative z-10">

                    {/* Top */}
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2 text-white/70 mb-3">
                                <Wallet className="w-4 h-4" />
                                <span className="text-xs font-semibold tracking-wide">
                                    MY WALLET
                                </span>
                            </div>

                            <p className="text-sm text-white/65">
                                Available balance
                            </p>

                            <div className="flex items-end gap-2 mt-1">
                                <span className="text-lg md:text-xl font-semibold text-white/80 mb-1">
                                    AED
                                </span>

                                <h1 className="text-4xl md:text-3xl font-black tracking-tight">
                                    {(walletSummary?.balance ?? 0).toFixed(2)}
                                </h1>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 ">
                            <div className="rounded-2xl bg-white/[0.08] border border-white/10 p-3.5">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <ArrowDownLeft className="w-4 h-4 text-[#C9E9AD]" />

                                    <span className="text-[11px] font-medium text-white/60">
                                        Total added
                                    </span>
                                </div>

                                <p className="text-base font-bold">
                                    AED {(walletSummary?.lifetimeCredited ?? 0).toFixed(2)}
                                </p>
                            </div>

                            <div className="rounded-2xl bg-white/[0.08] border border-white/10 p-3.5">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <ArrowUpRight className="w-4 h-4 text-orange-200" />

                                    <span className="text-[11px] font-medium text-white/60">
                                        Total spent
                                    </span>
                                </div>

                                <p className="text-base font-bold">
                                    AED {(walletSummary?.lifetimeDebited ?? 0).toFixed(2)}
                                </p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* ================= CONTENT ================= */}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                {/* ================= ADD MONEY ================= */}

                <div className="lg:col-span-5 bg-white border border-[#E7EDE4] rounded-[24px] p-5 md:p-6 shadow-[0_8px_30px_rgba(30,50,30,0.05)]">

                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-11 h-11 rounded-2xl bg-[#EEF6E9] flex items-center justify-center">
                            <Plus className="w-5 h-5 text-[#2F5E2E]" />
                        </div>

                        <div>
                            <h2 className="text-lg font-black text-gray-900">
                                Add Money
                            </h2>

                            <p className="text-xs text-gray-400 mt-0.5">
                                Choose an offer or enter your amount
                            </p>
                        </div>
                    </div>

                    {/* Offers */}
                    {offersData && offersData.length > 0 && (
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                                Quick Top-Up
                            </p>

                            <div className="grid grid-cols-2 gap-3">
                                {offersData.map((pkg) => {
                                    const isSelected = selectedOfferId === pkg.id;

                                    return (
                                        <button
                                            key={pkg.id}
                                            type="button"
                                            onClick={() => handleSelectOffer(pkg.id)}
                                            disabled={isSubmittingTopup}
                                            className={`
                        relative text-left rounded-2xl p-4 border-2
                        transition-all duration-200
                        ${isSelected
                                                    ? "border-[#2F5E2E] bg-[#F3F8F0] shadow-[0_6px_20px_rgba(47,94,46,0.10)]"
                                                    : "border-[#E8EDE5] bg-white hover:border-[#B8CEAD] hover:bg-[#FBFDF9]"
                                                }
                      `}
                                        >
                                            {/* Selected check */}
                                            {isSelected && (
                                                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-[#2F5E2E] flex items-center justify-center">
                                                    <Check className="w-3 h-3 text-white" />
                                                </div>
                                            )}
                                            {/* 
                                            <p className="text-[11px] text-gray-400 font-semibold pr-6 truncate">
                                                {pkg.name}
                                            </p> */}

                                            <div className="mt-2">
                                                <span className="text-xs font-bold text-[#2F5E2E]">
                                                    AED
                                                </span>

                                                <span className="text-2xl font-black text-gray-900 ml-1">
                                                    {pkg.topupAmount}
                                                </span>
                                            </div>

                                            {pkg.bonusAmount > 0 && (
                                                <div className="mt-3 inline-flex items-center gap-1.5 bg-[#E7F5DD] text-[#2F5E2E] px-2.5 py-1 rounded-full">
                                                    {/* <Sparkles className="w-3 h-3" /> */}

                                                    <span className="text-[10px] font-bold">
                                                        + AED {pkg.bonusAmount} bonus
                                                    </span>
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Divider */}
                    <div className="flex items-center gap-3 my-6">
                        <div className="h-px bg-gray-100 flex-1" />

                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            or enter amount
                        </span>

                        <div className="h-px bg-gray-100 flex-1" />
                    </div>

                    {/* Custom Amount */}
                    <div>
                        <TextInputComp
                            label="Custom Amount"
                            placeholder="Enter amount"
                            type="number"
                            value={customAmount}
                            onChangeText={handleCustomAmountChange}
                            disabled={isSubmittingTopup}
                        />

                        {customAmount && enteredAmount > 0 && (
                            <div className="mt-2 flex items-center gap-1.5 text-[#2F5E2E]">
                                <Check className="w-3.5 h-3.5" />

                                <span className="text-xs font-semibold">
                                    AED {enteredAmount.toFixed(2)} will be added to your wallet
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Payment Summary */}
                    {amountToPay > 0 && (
                        <div className="mt-5 rounded-2xl bg-[#F7F9F5] border border-[#E7EDE4] p-4">
                            <div className="flex justify-between items-center">
                                <span className="text-xs text-gray-500">
                                    You pay
                                </span>

                                <span className="text-sm font-bold text-gray-800">
                                    AED {amountToPay.toFixed(2)}
                                </span>
                            </div>

                            {bonusAmount > 0 && (
                                <div className="flex justify-between items-center mt-2">
                                    <span className="text-xs text-[#4F8A35]">
                                        Bonus
                                    </span>

                                    <span className="text-sm font-bold text-[#4F8A35]">
                                        + AED {bonusAmount.toFixed(2)}
                                    </span>
                                </div>
                            )}

                            <div className="border-t border-[#E2E8DF] mt-3 pt-3 flex justify-between items-center">
                                <span className="text-xs font-bold text-gray-700">
                                    Wallet receives
                                </span>

                                <span className="text-base font-black text-[#2F5E2E]">
                                    AED {finalWalletCredit.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Button */}
                    <CommonButton
                        text={
                            isSubmittingTopup
                                ? "Connecting to payment..."
                                : amountToPay > 0
                                    ? `Add AED ${amountToPay.toFixed(2)}`
                                    : "Add Money"
                        }
                        variant="filled"
                        className="w-full mt-5 py-2 text-sm font-bold rounded-xl"
                        onClick={handleAddWalletMoney}
                        disabled={isSubmittingTopup}
                    />

                    <div className="flex items-center justify-center gap-1.5 mt-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#6DBE45]" />

                        <p className="text-[10px] text-gray-400">
                            Secure payment · Balance updates instantly
                        </p>
                    </div>
                </div>

                {/* ================= HISTORY ================= */}

                <div className="lg:col-span-7 bg-white border border-[#E7EDE4] rounded-[24px] overflow-hidden shadow-[0_8px_30px_rgba(30,50,30,0.05)]">

                    {/* Header */}
                    <div className="px-5 md:px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-[#F3F6F1] flex items-center justify-center">
                                <History className="w-4.5 h-4.5 text-[#2F5E2E]" />
                            </div>

                            <div>
                                <h2 className="text-base font-black text-gray-900">
                                    Recent Activity
                                </h2>

                                <p className="text-[11px] text-gray-400 mt-0.5">
                                    Your latest wallet transactions
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Transactions */}
                    <div className="max-h-[560px] overflow-y-auto">
                        {!historyData || historyData.length === 0 ? (
                            <div className="min-h-[350px] flex flex-col items-center justify-center px-6">
                                <div className="w-16 h-16 rounded-full bg-[#F5F7F3] flex items-center justify-center mb-4">
                                    <Clock3 className="w-6 h-6 text-gray-300" />
                                </div>

                                <p className="text-sm font-bold text-gray-700">
                                    No transactions yet
                                </p>

                                <p className="text-xs text-gray-400 mt-1 text-center">
                                    Your wallet activity will appear here.
                                </p>
                            </div>
                        ) : (
                            <div>
                                {historyData.map((tx) => {
                                    const isCredit = tx.credit > 0;

                                    const transactionAmount = isCredit
                                        ? Number(tx.credit)
                                        : Number(tx.debit);

                                    return (
                                        <div
                                            key={tx.transactionId}
                                            className="group flex items-center gap-3 px-5 md:px-6 py-4 border-b border-gray-50 last:border-0 hover:bg-[#FAFCF8] transition-colors"
                                        >
                                            {/* Icon */}
                                            <div
                                                className={`
                          shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center
                          ${isCredit
                                                        ? "bg-[#ECF7E7] text-[#4F8A35]"
                                                        : "bg-[#FFF4E8] text-[#D97706]"
                                                    }
                        `}
                                            >
                                                {isCredit ? (
                                                    <ArrowDownLeft className="w-5 h-5" />
                                                ) : (
                                                    <ArrowUpRight className="w-5 h-5" />
                                                )}
                                            </div>

                                            {/* Information */}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-bold text-gray-800 truncate">
                                                    {tx.type || "Wallet Transaction"}
                                                </p>

                                                <p className="text-[11px] text-gray-400 truncate mt-0.5">
                                                    {tx.remarks}
                                                </p>

                                                <p className="text-[10px] text-gray-400 mt-1">
                                                    {new Date(tx.date).toLocaleDateString("en-US", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                    })}
                                                    {" · "}
                                                    {new Date(tx.date).toLocaleTimeString("en-US", {
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </p>
                                            </div>

                                            {/* Amount */}
                                            <div className="text-right shrink-0">
                                                <p
                                                    className={`text-sm font-black ${isCredit
                                                        ? "text-[#4F8A35]"
                                                        : "text-gray-900"
                                                        }`}
                                                >
                                                    {isCredit ? "+" : "-"} AED{" "}
                                                    {transactionAmount.toFixed(2)}
                                                </p>

                                                <p className="text-[10px] text-gray-400 mt-1">
                                                    Balance AED {Number(tx.balance).toFixed(2)}
                                                </p>
                                            </div>


                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WalletScreen;